import { fromJS, List, Record, RecordOf, Map } from 'immutable';
import moment from 'moment';
import { isNil } from 'lodash';

// CONSTANTS
import { DATETIME_FORMAT } from 'constants/formats.constants';
import { FUEL_LEVEL_DELTA_TRANSLATIONS } from 'constants/fuelLevels.constants';
import { STATUS_CODES } from 'constants/status.constants';

// UTILS
import { convertPercentageToIndex, convertIndexToPercentage } from 'utils/fuel.utils';
import { Agreement, DealershipSettings, FleetSettings } from 'services/resources';
import { BASE_TABLE_FIELD, TableFieldRecord } from 'components/FilteredTable/FilteredTable.component';

export type AgreementTableFieldRecord = TableFieldRecord<RecordOf<Agreement>>;

const makeAgreementTableFieldRecord = Record<AgreementTableFieldRecord>(BASE_TABLE_FIELD);

export enum AgreementStatus {
  BOOKED = 'draft',
  RESERVED = 'created',
  OPEN = 'open',
  CLOSED = 'closed',
  REOPENED = 'reopened',
}

export const AGREEMENT_STATUSES = {
  BOOKED: AgreementStatus.BOOKED,
  RESERVED: AgreementStatus.RESERVED,
  OPEN: AgreementStatus.OPEN,
  CLOSED: AgreementStatus.CLOSED,
  REOPENED: AgreementStatus.REOPENED,
} as const;

export const AGREEMENT_SIGNATURE_STATUS = {
  PENDING: 'pending',
  SIGNED: 'signed',
} as const;

export enum AGREEMENT_ACTIONS {
  SAVE = 'save',
  RESERVE = 'created',
  OPEN = 'open',
  CLOSE = 'close',
  REOPEN = 'reopen',
}

export const AGREEMENT_ACTION_NOTIFICATIONS = {
  [AGREEMENT_ACTIONS.SAVE]: 'views.Agreement.notifications.save',
  [AGREEMENT_ACTIONS.OPEN]: 'views.Agreement.notifications.open',
  [AGREEMENT_ACTIONS.CLOSE]: 'views.Agreement.notifications.close',
  [AGREEMENT_ACTIONS.REOPEN]: 'views.Agreement.notifications.reopen',
};

export const AGREEMENT_STATUS_TAG_MAPPING = {
  [AgreementStatus.BOOKED]: STATUS_CODES.NEUTRAL,
  [AgreementStatus.RESERVED]: STATUS_CODES.PENDING,
  [AgreementStatus.OPEN]: STATUS_CODES.SUCCESS,
  [AgreementStatus.CLOSED]: STATUS_CODES.ERROR,
  [AgreementStatus.REOPENED]: STATUS_CODES.SUCCESS,
} as const;

export const AGREEMENT_SIGNATURE_STATUS_TRANSLATION = {
  [AGREEMENT_SIGNATURE_STATUS.PENDING]: {
    translationId: 'services.Agreement.signature.statuses.pending',
  },
  [AGREEMENT_SIGNATURE_STATUS.SIGNED]: {
    translationId: 'services.Agreement.signature.statuses.signed',
  },
} as const;

export const AGREEMENT_STATUS_TRANSLATIONS = {
  [AgreementStatus.BOOKED]: {
    translationId: 'services.Agreement.statuses.booked',
  },
  [AgreementStatus.RESERVED]: {
    translationId: 'services.Agreement.statuses.reserved',
  },
  [AgreementStatus.OPEN]: {
    translationId: 'services.Agreement.statuses.open',
  },
  [AgreementStatus.CLOSED]: {
    translationId: 'services.Agreement.statuses.closed',
  },
  [AgreementStatus.REOPENED]: {
    translationId: 'services.Agreement.statuses.reopened',
  },
};

const dateSerializer = (value) => {
  const stringValue = String(value);
  if (stringValue.includes(',')) {
    return stringValue
      .split(',')
      .map((value) => moment(value).toISOString())
      .join(',');
  }

  return moment(stringValue).toISOString();
};
const dateTimeDeserializer = (value) => {
  const stringValue = String(value);
  if (stringValue.includes(',')) {
    return stringValue
      .split(',')
      .map((value) => moment(value).format(DATETIME_FORMAT))
      .join(',');
  }
  return moment(stringValue).format(DATETIME_FORMAT);
};

const FUEL_LEVEL_DELTA_TRANSLATIONS_MAPPING = {};

Object.keys(FUEL_LEVEL_DELTA_TRANSLATIONS).forEach((key) => {
  FUEL_LEVEL_DELTA_TRANSLATIONS_MAPPING[key] = {
    translationId: FUEL_LEVEL_DELTA_TRANSLATIONS[key],
  };
});

export const AGREEMENTS_TABLE_FIELDS = List([
  makeAgreementTableFieldRecord({
    name: 'id',
    dataPath: 'id',
    title: { translationId: 'services.Agreements.number' },
    type: 'number',
    link: '/fleet/agreements',
    linkAttributePath: 'id',
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'dealership',
    dataPath: (agreement, context) => {
      const customerDealershipId = agreement.get('dealership');
      const activeDealership = context.get('dealership');
      const hierarchyValues = context.get('hierarchyValues');

      const hierarchyEntity = hierarchyValues.find((entity) => entity.get('id') === customerDealershipId);
      return hierarchyEntity?.get('name') ?? activeDealership.get('name');
    },
    title: { translationId: 'views.tableFields.dealership' },
    type: 'string',
    sortable: true,
    hierarchyOnly: true,
    excludeOperators: ['isKnown', 'isUnknown'],
  }),
  makeAgreementTableFieldRecord({
    name: 'ro_id',
    dataPath: 'roId',
    title: { translationId: 'views.Agreement.roNumber' },
    type: 'number',
    filterable: false,
    sortable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'status',
    dataPath: 'status',
    title: { translationId: 'services.Agreements.status' },
    type: 'dropdown',
    values: AGREEMENT_STATUS_TRANSLATIONS,
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'expected_date_out',
    dataPath: 'expectedDateOut',
    title: { translationId: 'services.Agreements.expectedDateOut' },
    type: 'dateTime',
    serialize: dateSerializer,
    deserialize: dateTimeDeserializer,
    sortable: true,
    hidden: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'date_out',
    dataPath: 'dateOut',
    title: { translationId: 'services.Agreements.dateOut' },
    type: 'dateTime',
    serialize: dateSerializer,
    deserialize: dateTimeDeserializer,
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'date_due',
    dataPath: 'dateDue',
    title: { translationId: 'services.Agreements.dateDue' },
    type: 'dateTime',
    serialize: dateSerializer,
    deserialize: dateTimeDeserializer,
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'date_in',
    dataPath: 'dateIn',
    title: { translationId: 'services.Agreements.dateIn' },
    type: 'dateTime',
    serialize: dateSerializer,
    deserialize: dateTimeDeserializer,
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'days_open',
    dataPath: 'daysOpen',
    title: { translationId: 'services.Agreements.daysOpen' },
    type: 'number',
    sortable: false,
    filterable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'days_overdue',
    dataPath: 'daysOverdue',
    title: { translationId: 'services.Agreements.daysOverdue' },
    type: 'number',
    sortable: false,
    filterable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_signature_status',
    dataPath: (agreement) => agreement.get('customerSignatureStatus'),
    title: { translationId: 'services.Agreements.signatureStatus' },
    values: AGREEMENT_SIGNATURE_STATUS_TRANSLATION,
    type: 'dropdown',
    filterable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'fuel_used',
    title: { translationId: 'views.Agreement.fuelUsed' },
    type: 'fuelDropdown',
    dataPath: (agreement) => {
      const fuelUsed = agreement.get('fuelUsed');

      if (!isNil(fuelUsed)) {
        return convertPercentageToIndex(fuelUsed);
      }
      return '';
    },
    values: FUEL_LEVEL_DELTA_TRANSLATIONS_MAPPING,
    serialize: convertIndexToPercentage,
    deserialize: convertPercentageToIndex,
  }),
  makeAgreementTableFieldRecord({
    name: 'balance_due',
    dataPath: 'balanceDue',
    title: { translationId: 'services.Agreements.balanceDue' },
    type: 'money',
    filterable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'balance_paid',
    dataPath: 'balancePaid',
    title: { translationId: 'services.Agreements.balancePaid' },
    type: 'money',
    filterable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'advisor_id',
    dataPath: 'advisorId',
    title: { translationId: 'views.Agreement.advisorId' },
    type: 'number',
  }),
  makeAgreementTableFieldRecord({
    name: 'advisor_first_name',
    dataPath: 'advisorFirstName',
    title: { translationId: 'views.Agreement.advisorFirstName' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'advisor_last_name',
    dataPath: 'advisorLastName',
    title: { translationId: 'views.Agreement.advisorLastName' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'agent_out__first_name',
    dataPath: 'agentOut.firstName',
    title: { translationId: 'services.Agreements.agentOutFirstName' },
    type: 'string',
    sortable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'agent_out__last_name',
    dataPath: 'agentOut.lastName',
    title: { translationId: 'services.Agreements.agentOutLastName' },
    type: 'string',
    sortable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'agent_in__first_name',
    dataPath: 'agentIn.firstName',
    title: { translationId: 'services.Agreements.agentInFirstName' },
    type: 'string',
    sortable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'agent_in__last_name',
    dataPath: 'agentIn.lastName',
    title: { translationId: 'services.Agreements.agentInLastName' },
    type: 'string',
    sortable: false,
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_source',
    dataPath: 'customerSource',
    title: { translationId: 'services.Agreements.customerId' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_first_name',
    dataPath: 'customerFirstName',
    title: { translationId: 'services.Agreements.customerFirstName' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_last_name',
    dataPath: 'customerLastName',
    title: { translationId: 'services.Agreements.customerLastName' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_phone_residential',
    dataPath: 'customerPhoneResidential',
    title: { translationId: 'services.Agreements.customerPhoneResidential' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_phone_cell',
    dataPath: 'customerPhoneCell',
    title: { translationId: 'services.Agreements.customerPhoneMobile' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'customer_phone_business',
    dataPath: 'customerPhoneBusiness',
    title: { translationId: 'services.Agreements.customerPhoneBusiness' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_stock_number',
    dataPath: 'vehicleStockNumber',
    title: { translationId: 'services.Agreements.vehicleStockNumber' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_license_plate',
    dataPath: 'vehicleLicensePlate',
    title: { translationId: 'services.Vehicles.licensePlate' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_vin',
    dataPath: 'vehicleVin',
    title: { translationId: 'services.Agreements.vehicleVin' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_year',
    dataPath: 'vehicleYear',
    title: { translationId: 'services.Agreements.vehicleYear' },
    type: 'number',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_model',
    dataPath: 'vehicleModel',
    title: { translationId: 'services.Agreements.vehicleModel' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_make',
    dataPath: 'vehicleMake',
    title: { translationId: 'services.Agreements.vehicleMake' },
    type: 'string',
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_department',
    dataPath: (agreement, context) => {
      const id = agreement.get('vehicleDepartment');

      if (!isNil(id)) {
        const departments = context.get('departments', fromJS([]));
        const matchedEntry = departments.find((department) => department.get('id') === id, null, fromJS({}));

        return matchedEntry.get('name', '');
      }

      return '';
    },

    title: { translationId: 'services.Agreements.vehicleDepartment' },
    type: 'string',
    filterable: false,
    sortable: true,
  }),
  makeAgreementTableFieldRecord({
    name: 'vehicle_tag_number',
    dataPath: 'vehicleTagNumber',
    title: { translationId: 'services.Agreements.vehicleTagNumber' },
    type: 'string',
    filterable: false,
  }),
]);

export const DEFAULT_AGREEMENT_COLUMNS = List(
  AGREEMENTS_TABLE_FIELDS.map((tableField, index) =>
    Map({
      field: tableField.get('name'),
      position: index,
      disabled: false,
    })
  )
);

const DRAFT_REQUIRED_FIELDS = ['customerFirstName', 'customerLastName', 'customerPhoneCell', 'dateDue'];

const CREATED_REQUIRED_FIELDS = [...DRAFT_REQUIRED_FIELDS];

const OPEN_REQUIRED_FIELDS = [
  ...CREATED_REQUIRED_FIELDS,
  'agentOut',
  'customerAddress1',
  'customerCity',
  'customerCountry',
  'customerLicenseExpires',
  'customerLicenseNumber',
  'customerLicenseState',
  'customerPostalCode',
  'customerState',
  'odometerOut',
  'fuelOut',
  'batteryLevelOut',
  'vehicleVin',
];

const INSURANCE_REQUIRED_FIELDS = [
  'customerInsuranceCompanyName',
  'customerInsurancePolicyNumber',
  'customerInsurancePolicyExpiry',
  'customerInsuranceProvince',
];

export const CREDIT_CARD_REQUIRED_FIELDS = [
  'creditcardBrand',
  'creditcardLast4',
  'creditcardExpMonth',
  'creditcardExpYear',
  'creditcardPostalCode',
  'creditcardSource',
];

const CLOSED_REQUIRED_FIELDS = [...OPEN_REQUIRED_FIELDS, 'odometerIn', 'fuelIn', 'batteryLevelIn'];

export const CURRENT_AGREEMENT_FIELD_REQUIREMENT = Map({
  [AgreementStatus.BOOKED]: DRAFT_REQUIRED_FIELDS,
  [AgreementStatus.RESERVED]: CREATED_REQUIRED_FIELDS,
  [AgreementStatus.OPEN]: OPEN_REQUIRED_FIELDS,
  [AgreementStatus.CLOSED]: CLOSED_REQUIRED_FIELDS,
  [AgreementStatus.REOPENED]: OPEN_REQUIRED_FIELDS,
});

export const NEXT_AGREEMENT_FIELD_REQUIREMENT = Map({
  [AgreementStatus.BOOKED]: CREATED_REQUIRED_FIELDS,
  [AgreementStatus.RESERVED]: OPEN_REQUIRED_FIELDS,
  [AgreementStatus.OPEN]: CLOSED_REQUIRED_FIELDS,
  [AgreementStatus.CLOSED]: CLOSED_REQUIRED_FIELDS,
  [AgreementStatus.REOPENED]: CLOSED_REQUIRED_FIELDS,
});

export function getRequiredFields(
  status: AgreementStatus,
  settings: RecordOf<FleetSettings>,
  dealershipSettings: RecordOf<DealershipSettings>
): [current: string[], next: string[]] {
  const currentFields = CURRENT_AGREEMENT_FIELD_REQUIREMENT.get(status) || [];
  const nextFields = NEXT_AGREEMENT_FIELD_REQUIREMENT.get(status) || [];

  const stripeEnabled =
    dealershipSettings.get('paymentAccountId') &&
    dealershipSettings.get('paymentsAcceptedTerms') &&
    dealershipSettings.get('paymentsEnabled');

  if (status === AgreementStatus.RESERVED) {
    // If agreement is reserved, require insurance and credit card fields for future status (OPEN)
    if (settings.get('requireInsuranceOnAgreement')) {
      nextFields.push(...INSURANCE_REQUIRED_FIELDS);
    }
    if (settings.get('requireCreditCardOnAgreement') && stripeEnabled) {
      nextFields.push(...CREDIT_CARD_REQUIRED_FIELDS);
    }
  }

  return [currentFields, nextFields];
}

export const BASE_DEFAULT_DAYS_OUT = 1;
